import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  MousePointer2, Hand, Ruler, MapPin, Pencil,
  Upload, Globe, Layers, Database, Grid3x3, Grid2x2,
  CircleDashed, Crosshair, Combine, Pentagon, Scissors, Merge,
  Activity, Target, Workflow, BarChart3, TrendingUp, Waypoints,
  Table2, ListChecks, Bot, Mountain, Box, FileText, ClipboardList,
  Download, Printer, Link2, Image,
  Map as MapIcon, Satellite, Flame, Compass, ScanSearch,
  Settings2, LayoutGrid, Type, Maximize2, LocateFixed, Columns2,
  PanelLeft, PanelRight, PanelBottom, RotateCcw, FlaskConical, Search, FolderOpen,
  FilePlus2, History, Save, RefreshCw, Undo2
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useFeature } from '../lib/runtimeConfig';
import { useDemoMode } from '../hooks/useDemoMode';
import AddressSearch from './AddressSearch';
import OpenDatasetPopover, { popoverPosBelow } from './map/OpenDatasetPopover';
import { loadRecent, viewer3dPath as viewer3dPathFor } from '../lib/map-session';

/** /3d keeps the map's project so it can list the project's stored inversion models. */
const viewer3dPath = () => viewer3dPathFor(new URLSearchParams(window.location.search).get('project'));

// ─── Display modes for ribbon buttons ───
const DISPLAY_MODES = [
  { id: 'both', label: 'Icon + Name', icon: LayoutGrid },
  { id: 'icon', label: 'Icon Only', icon: Maximize2 },
  { id: 'name', label: 'Name Only', icon: Type },
];

// ─── Tab definitions (ArcGIS Pro-inspired) ───
const TABS = [
  { id: 'file', label: 'File' },
  { id: 'map', label: 'Map' },
  { id: 'insert', label: 'Insert' },
  { id: 'edit', label: 'Edit' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'view', label: 'View' },
  { id: 'reports', label: 'Reports' },
  { id: 'share', label: 'Share' },
];

// ─── Ribbon content per tab ───
/** Popover under a ribbon button (fixed, so the ribbon's horizontal scroll cannot clip it). */
function FindAddressPopover({ pos, onSelect, onClose, wrapRef }) {
  const { C } = useTheme();
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || wrapRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose, wrapRef]);
  return (
    <div ref={ref} role="dialog" aria-label="Find address"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 340, maxWidth: 'calc(100vw - 16px)', zIndex: 1000, padding: 10, borderRadius: 8,
        background: C.bg2, border: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
      <AddressSearch autoFocus onSelect={(r) => { onSelect(r); onClose(); }} />
    </div>
  );
}

/** Recent maps: the last 8 datasets/projects opened (lib/map-session.js), newest first. */
function RecentPopover({ pos, onClose, wrapRef }) {
  const { C } = useTheme();
  const ref = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [items] = useState(loadRecent);
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || wrapRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose, wrapRef]);
  const open = (it) => {
    const next = new URLSearchParams(searchParams);
    if (it.project) next.set('project', it.project); else next.delete('project');
    if (it.dataset) next.set('dataset', it.dataset); else next.delete('dataset');
    setSearchParams(next);
    onClose();
  };
  return (
    <div ref={ref} role="dialog" aria-label="Recent maps"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 340, maxWidth: 'calc(100vw - 16px)', zIndex: 1000, padding: 10, borderRadius: 8,
        background: C.bg2, border: `1px solid ${C.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }}>
      <div className="text-[11px]" style={{ color: C.text1 }}>
        {items.length === 0 ? <div>No recent maps yet</div> : items.map((it) => (
          <button key={`${it.project || ''}/${it.dataset || ''}`} type="button" data-testid="recent-map" onClick={() => open(it)}
            className="flex w-full items-center justify-between gap-2 px-2 py-1 rounded text-left hover:bg-white/5" style={{ color: C.text0 }}>
            <span className="truncate">{it.name}</span>
            <span className="flex-shrink-0" style={{ color: C.text2 }}>{it.dataset ? 'Dataset' : 'Project'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function FileRibbon({ onNewMap, onImportFile, onSaveLayers, saveDisabledWhy, autosave, onToggleAutosave, onRestoreSession, canRestoreSession, displayMode }) {
  const [openPos, setOpenPos] = useState(null);
  const [recentPos, setRecentPos] = useState(null);
  const openRef = useRef(null);
  const recentRef = useRef(null);
  const closeOpen = useCallback(() => setOpenPos(null), []);
  const closeRecent = useCallback(() => setRecentPos(null), []);
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      <RibbonGroup label="Map">
        <RibbonBtn icon={FilePlus2} label="New map" tooltip="Clear the map and start again" onClick={() => onNewMap?.()} displayMode={displayMode} size="lg" />
        <div ref={openRef} className="flex-shrink-0">
          <RibbonBtn icon={FolderOpen} label="Open…" tooltip="Open a project or a dataset on the map" active={!!openPos} onClick={() => { setRecentPos(null); setOpenPos(openPos ? null : popoverPosBelow(openRef.current)); }} displayMode={displayMode} size="lg" />
        </div>
        {openPos && <OpenDatasetPopover pos={openPos} wrapRef={openRef} onClose={closeOpen} />}
        <div ref={recentRef} className="flex-shrink-0">
          <RibbonBtn icon={History} label="Recent" tooltip="The last maps you opened" active={!!recentPos} onClick={() => { setOpenPos(null); setRecentPos(recentPos ? null : popoverPosBelow(recentRef.current)); }} displayMode={displayMode} size="lg" />
        </div>
        {recentPos && <RecentPopover pos={recentPos} wrapRef={recentRef} onClose={closeRecent} />}
        <RibbonBtn icon={Upload} label="Import file…" onClick={onImportFile} displayMode={displayMode} size="lg" />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Save">
        <RibbonBtn icon={Save} label="Save layers to project…" tooltip={saveDisabledWhy || 'Save the layers in Viewing (not saved) as project datasets'} disabled={!!saveDisabledWhy} onClick={onSaveLayers} displayMode={displayMode} size="lg" />
        <RibbonBtn icon={RefreshCw} label="Auto-save session" tooltip="Keep your map (layers, imports, results) and restore it when you come back" active={!!autosave} pressed={!!autosave} onClick={() => onToggleAutosave?.(!autosave)} displayMode={displayMode} size="lg" />
        <RibbonBtn icon={Undo2} label="Restore last session" tooltip={canRestoreSession ? 'Restore the map you had last' : 'No saved session to restore'} disabled={!canRestoreSession} onClick={onRestoreSession} displayMode={displayMode} size="lg" />
      </RibbonGroup>
    </div>
  );
}

function MapRibbon({ viewMode, setViewMode, showHeatmap, setShowHeatmap, tool, setTool, coordSys, setCoordSys, CRS_OPTIONS, onFindAddress, displayMode }) {
  const { C } = useTheme();
  const [findPos, setFindPos] = useState(null); // popover position while Find address is open
  const findRef = useRef(null);
  const closeFind = useCallback(() => setFindPos(null), []);
  const toggleFind = () => {
    if (findPos) { setFindPos(null); return; }
    const r = findRef.current?.getBoundingClientRect();
    setFindPos({ top: (r?.bottom ?? 80) + 6, left: Math.max(8, Math.min(r?.left ?? 8, window.innerWidth - 356)) });
  };
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      <RibbonGroup label="Tools">
        <RibbonBtn icon={MousePointer2} label="Select" active={tool === 'select'} onClick={() => setTool('select')} displayMode={displayMode} />
        <RibbonBtn icon={Hand} label="Pan" active={tool === 'pan'} onClick={() => setTool('pan')} displayMode={displayMode} />
        <RibbonBtn icon={Ruler} label="Measure" active={tool === 'measure'} onClick={() => setTool('measure')} displayMode={displayMode} />
        <RibbonBtn icon={LocateFixed} label="Find nearest" active={tool === 'nearest'} onClick={() => setTool('nearest')} displayMode={displayMode} />
        <RibbonBtn icon={MapPin} label="Annotate" active={tool === 'annotate'} onClick={() => setTool('annotate')} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Basemap">
        <RibbonBtn icon={MapIcon} label="Map" active={viewMode === 'map'} onClick={() => setViewMode('map')} displayMode={displayMode} />
        <RibbonBtn icon={Satellite} label="Satellite" active={viewMode === 'satellite'} onClick={() => setViewMode('satellite')} displayMode={displayMode} />
        <RibbonBtn icon={Compass} label="Topo" active={viewMode === 'topo'} onClick={() => setViewMode('topo')} displayMode={displayMode} />
        <RibbonBtn icon={Flame} label="Heatmap" active={showHeatmap} onClick={() => setShowHeatmap(v => !v)} tone="amber" displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Reference">
        <select value={coordSys} onChange={e => setCoordSys(e.target.value)}
          className="h-7 px-2 text-[11px] rounded-md outline-none"
          style={{ background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 }}>
          {CRS_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <div ref={findRef} className="flex-shrink-0">
          <RibbonBtn icon={Search} label="Find address" tooltip="Find an address or place and fly the map there" active={!!findPos} onClick={toggleFind} displayMode={displayMode} />
        </div>
        {findPos && <FindAddressPopover pos={findPos} wrapRef={findRef} onClose={closeFind} onSelect={(r) => onFindAddress?.(r)} />}
      </RibbonGroup>
    </div>
  );
}

function InsertRibbon({ onImportFile, onAddService, onGeoreference, onShowInversion, showInversionPanel, displayMode }) {
  const inversionEnabled = useFeature('inversion');
  const [openPos, setOpenPos] = useState(null);
  const openRef = useRef(null);
  const closeOpen = useCallback(() => setOpenPos(null), []);
  const toggleOpen = () => {
    if (openPos) { setOpenPos(null); return; }
    setOpenPos(popoverPosBelow(openRef.current));
  };
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      <RibbonGroup label="Data">
        <div ref={openRef} className="flex-shrink-0">
          <RibbonBtn icon={FolderOpen} label="Open" tooltip="Open a project or a dataset on the map" active={!!openPos} onClick={toggleOpen} displayMode={displayMode} size="lg" />
        </div>
        {openPos && <OpenDatasetPopover pos={openPos} wrapRef={openRef} onClose={closeOpen} />}
        <RibbonBtn icon={Upload} label="Import File" onClick={onImportFile} displayMode={displayMode} size="lg" />
        <RibbonBtn icon={Image} label="Georeference" onClick={onGeoreference} displayMode={displayMode} size="lg" />
        {inversionEnabled && <RibbonBtn icon={Activity} label="Inversion results" tooltip="Import and view EarthImager, BERT and 1D inversion results. Inversions are run in EarthImager." active={showInversionPanel} onClick={onShowInversion} displayMode={displayMode} size="lg" />}
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Services">
        <RibbonBtn icon={Grid2x2} label="XYZ Tiles" onClick={() => onAddService('xyz')} displayMode={displayMode} />
        <RibbonBtn icon={Layers} label="WMS" onClick={() => onAddService('wms')} displayMode={displayMode} />
        <RibbonBtn icon={Database} label="WFS" onClick={() => onAddService('wfs')} displayMode={displayMode} />
        <RibbonBtn icon={Grid3x3} label="WMTS" onClick={() => onAddService('wmts')} displayMode={displayMode} />
        <RibbonBtn icon={Globe} label="ArcGIS" onClick={() => onAddService('arcgis')} displayMode={displayMode} />
        <RibbonBtn icon={MapPin} label="OSM" onClick={() => onAddService('osm')} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function EditRibbon({ activeDrawMode, onDrawMode, tool, setTool, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      <RibbonGroup label="Create Features">
        <RibbonBtn icon={MapPin} label="Point" active={activeDrawMode === 'point'} onClick={() => onDrawMode('point')} displayMode={displayMode} />
        <RibbonBtn icon={Pencil} label="Line" active={activeDrawMode === 'line'} onClick={() => onDrawMode('line')} displayMode={displayMode} />
        <RibbonBtn icon={Pentagon} label="Polygon" active={activeDrawMode === 'polygon'} onClick={() => onDrawMode('polygon')} displayMode={displayMode} />
        <RibbonBtn icon={ScanSearch} label="Rectangle" active={activeDrawMode === 'rectangle'} onClick={() => onDrawMode('rectangle')} displayMode={displayMode} />
        <RibbonBtn icon={CircleDashed} label="Circle" active={activeDrawMode === 'circle'} onClick={() => onDrawMode('circle')} displayMode={displayMode} />
        <RibbonBtn icon={Pencil} label="Freehand" active={activeDrawMode === 'freehand'} onClick={() => onDrawMode('freehand')} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Sketching">
        <RibbonBtn icon={Ruler} label="Measure" active={tool === 'measure'} onClick={() => setTool('measure')} displayMode={displayMode} />
        <RibbonBtn icon={MapPin} label="Annotate" active={tool === 'annotate'} onClick={() => setTool('annotate')} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function AnalysisRibbon({ onShowAnalysisPanel, onShowModelBuilder, onShowGridCombine, onShowGridProfile, showGridProfile, onShowVariogram, onShowSectionView, showAnalysisPanel, showGridCombine, showVariogram, showSectionView, rawDatasetId, pseudosectionOpen, onTogglePseudosection, displayMode }) {
  const analysisEnabled = useFeature('analysis');
  const spatialStatsEnabled = useFeature('spatial_stats');
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      {/* Geoprocessing */}
      <RibbonGroup label="Geoprocessing">
        <RibbonBtn icon={CircleDashed} label="Buffer" onClick={() => onShowAnalysisPanel('buffer')} displayMode={displayMode} />
        <RibbonBtn icon={Crosshair} label="Centroids" onClick={() => onShowAnalysisPanel('centroids')} displayMode={displayMode} />
        <RibbonBtn icon={Combine} label="Dissolve" onClick={() => onShowAnalysisPanel('dissolve')} displayMode={displayMode} />
        <RibbonBtn icon={Pentagon} label="Convex Hull" onClick={() => onShowAnalysisPanel('convex')} displayMode={displayMode} />
        <RibbonBtn icon={Scissors} label="Clip" onClick={() => onShowAnalysisPanel('clip')} displayMode={displayMode} />
        <RibbonBtn icon={Merge} label="Merge" onClick={() => onShowAnalysisPanel('merge')} displayMode={displayMode} />
        <RibbonBtn icon={Target} label="Nearest" onClick={() => onShowAnalysisPanel('nearest')} displayMode={displayMode} />
        <RibbonBtn icon={Activity} label="Heatmap" onClick={() => onShowAnalysisPanel('heatmap')} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      {/* Geostatistics */}
      <RibbonGroup label="Geostatistics">
        {analysisEnabled && <RibbonBtn icon={TrendingUp} label="Kriging" onClick={() => onShowAnalysisPanel('kriging')} displayMode={displayMode} />}
        {analysisEnabled && <RibbonBtn icon={BarChart3} label="Variogram" active={showVariogram} onClick={() => onShowVariogram()} displayMode={displayMode} />}
        {analysisEnabled && <RibbonBtn icon={Waypoints} label="IDW" onClick={() => onShowAnalysisPanel('idw')} displayMode={displayMode} />}
        {spatialStatsEnabled && <RibbonBtn icon={Activity} label="Hot Spot" onClick={() => onShowAnalysisPanel('hotspot')} displayMode={displayMode} />}
        <RibbonBtn icon={Grid3x3} label="Grid Math" active={showGridCombine} onClick={() => onShowGridCombine()} displayMode={displayMode} />
        <RibbonBtn icon={Ruler} label="Grid Profile" active={showGridProfile} onClick={() => onShowGridProfile()} displayMode={displayMode} />
        <RibbonBtn icon={TrendingUp} label="Profile" active={showSectionView} onClick={() => onShowSectionView()} displayMode={displayMode} />
        <RibbonBtn icon={Layers} label="Pseudosection" active={pseudosectionOpen} disabled={!rawDatasetId} tooltip={rawDatasetId ? 'Pseudosection of the raw readings (measured values by pseudo-depth)' : 'Open a raw survey dataset on the map to show its pseudosection'} onClick={() => onTogglePseudosection()} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      {/* Workflows */}
      <RibbonGroup label="Workflows">
        <RibbonBtn icon={Workflow} label="Model Builder" onClick={() => onShowModelBuilder()} displayMode={displayMode} size="lg" />
        <RibbonBtn icon={ScanSearch} label="All Tools" active={showAnalysisPanel} onClick={() => onShowAnalysisPanel('all')} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function ViewRibbon({ showLayerPanel, setShowLayerPanel, showWorkflowPanel, setShowWorkflowPanel, showAgentPanel, setShowAgentPanel, showAttributeTable, setShowAttributeTable, showChartPanel, setShowChartPanel, showStyleEditor, setShowStyleEditor, navigate, showCompare, onToggleCompare, displayMode, dockCollapsed, onToggleDock, onResetLayout, showViewer3dPanel, onToggleViewer3d }) {
  const inversionEnabled = useFeature('inversion');
  const [demoMode, setDemoMode] = useDemoMode();
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      <RibbonGroup label="Windows">
        <RibbonBtn icon={Layers} label="Layers" active={showLayerPanel} onClick={() => setShowLayerPanel(v => !v)} displayMode={displayMode} />
        <RibbonBtn icon={ListChecks} label="Workflow" active={showWorkflowPanel} onClick={() => setShowWorkflowPanel(v => !v)} displayMode={displayMode} />
        <RibbonBtn icon={Table2} label="Attributes" active={showAttributeTable} onClick={() => setShowAttributeTable(v => !v)} displayMode={displayMode} />
        <RibbonBtn icon={BarChart3} label="Charts" active={showChartPanel} onClick={() => setShowChartPanel(v => !v)} displayMode={displayMode} />
        <RibbonBtn icon={Settings2} label="Properties" active={showStyleEditor} onClick={() => setShowStyleEditor(v => !v)} displayMode={displayMode} />
        <RibbonBtn icon={Bot} label="Field Agents" active={showAgentPanel} onClick={() => setShowAgentPanel(v => !v)} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Compare">
        <RibbonBtn icon={Columns2} label="Split / Swipe" active={showCompare} onClick={() => onToggleCompare?.()} displayMode={displayMode} size="lg" />
      </RibbonGroup>

      <RibbonDivider />

      {onToggleDock && (
        <>
          <RibbonGroup label="Docks">
            <RibbonBtn icon={PanelLeft} label="Left dock" active={!dockCollapsed?.left} onClick={() => onToggleDock('left')} displayMode={displayMode} />
            <RibbonBtn icon={PanelRight} label="Right dock" active={!dockCollapsed?.right} onClick={() => onToggleDock('right')} displayMode={displayMode} />
            <RibbonBtn icon={PanelBottom} label="Bottom dock" active={!dockCollapsed?.bottom} onClick={() => onToggleDock('bottom')} displayMode={displayMode} />
            <RibbonBtn icon={RotateCcw} label="Reset layout" onClick={() => onResetLayout?.()} displayMode={displayMode} />
          </RibbonGroup>

          <RibbonDivider />
        </>
      )}

      <RibbonGroup label="Sample data">
        <RibbonBtn icon={FlaskConical} label={demoMode ? 'Demo: on' : 'Demo: off'} active={demoMode} onClick={() => setDemoMode(!demoMode)} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Scene">
        {inversionEnabled && onToggleViewer3d && <RibbonBtn icon={Box} label="3D view" tooltip="Dock the 3D view beside the map (right dock). Sync camera keeps the map and the 3D view in step." active={showViewer3dPanel} onClick={onToggleViewer3d} displayMode={displayMode} size="lg" />}
        {inversionEnabled && <RibbonBtn icon={Mountain} label="3D Viewer" tooltip="Open the full-page 3D viewer" onClick={() => navigate(viewer3dPath())} displayMode={displayMode} size="lg" />}
      </RibbonGroup>
    </div>
  );
}

function ReportsRibbon({ onShowMapLayout, onShowReportBuilder, onShowProjectReport, onQuickExport, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      {/* Print / Layout */}
      <RibbonGroup label="Layout">
        <RibbonBtn icon={FileText} label="Report Builder" onClick={onShowReportBuilder} displayMode={displayMode} size="lg" />
        <RibbonBtn icon={Printer} label="Print Layout" onClick={onShowMapLayout} displayMode={displayMode} size="lg" />
        <RibbonBtn icon={Image} label="Quick Export" onClick={onQuickExport} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      {/* Project Reports */}
      <RibbonGroup label="Project Reports">
        <RibbonBtn icon={FileText} label="Project Summary" onClick={() => onShowProjectReport('summary')} displayMode={displayMode} />
        <RibbonBtn icon={ClipboardList} label="Field Report" onClick={() => onShowProjectReport('field')} displayMode={displayMode} />
        <RibbonBtn icon={BarChart3} label="QC Report" onClick={() => onShowProjectReport('qc')} displayMode={displayMode} />
        <RibbonBtn icon={Activity} label="Anomaly Report" onClick={() => onShowProjectReport('anomaly')} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function ShareRibbon({ onExportPng, onExportGeoJSON, onExportCSV, onCopyLink, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[50px] overflow-x-auto">
      <RibbonGroup label="Export">
        <RibbonBtn icon={Image} label="Map PNG" onClick={onExportPng} displayMode={displayMode} />
        <RibbonBtn icon={Download} label="GeoJSON" onClick={onExportGeoJSON} displayMode={displayMode} />
        <RibbonBtn icon={Download} label="CSV" onClick={onExportCSV} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Sharing">
        <RibbonBtn icon={Link2} label="Copy Link" onClick={onCopyLink} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

// ─── Shared sub-components ───
export function RibbonGroup({ label, children }) {
  const { C } = useTheme();
  return (
    <div className="flex flex-col items-center flex-shrink-0">
      <div className="flex items-center gap-[3px] px-2 py-[2px]">
        {children}
      </div>
      <span className="text-[9px] font-medium tracking-wide uppercase leading-none mt-[2px]" style={{ color: C.text2 }}>{label}</span>
    </div>
  );
}

export function RibbonBtn({ icon: Icon, label, active, onClick, size, tone, disabled, displayMode = 'both', tooltip, pressed }) {
  const { C, theme } = useTheme();
  const isLg = size === 'lg';
  const light = theme === 'light';
  // Active state: the theme's accent (dark cyan on the light theme, where Tailwind's cyan-400 is unreadable on white).
  const activeStyle = tone === 'amber'
    ? { color: light ? '#b45309' : '#fbbf24', background: light ? 'rgba(217,119,6,0.12)' : 'rgba(245,158,11,0.15)', borderColor: light ? 'rgba(217,119,6,0.45)' : 'rgba(245,158,11,0.3)', boxShadow: light ? 'none' : '0 0 8px rgba(210,153,34,0.25)' }
    : { color: C.cyan, background: light ? 'rgba(8,145,178,0.12)' : 'rgba(6,182,212,0.15)', borderColor: light ? 'rgba(8,145,178,0.45)' : 'rgba(6,182,212,0.3)', boxShadow: light ? 'none' : '0 0 8px rgba(0,212,255,0.25)' };
  const inactiveColor = light ? 'hover:bg-black/5 border-transparent' : 'hover:text-slate-200 hover:bg-white/5 border-transparent';

  const showIcon = displayMode === 'both' || displayMode === 'icon';
  const showLabel = displayMode === 'both' || displayMode === 'name' || isLg;

  // Sizing based on display mode
  const sizeClass = displayMode === 'icon'
    ? (isLg ? 'w-9 h-8' : 'w-7 h-7')
    : displayMode === 'name'
      ? 'h-7 px-2'
      : (isLg ? 'min-w-12 h-8 px-2 gap-[2px]' : 'h-7 px-[6px] gap-[3px]');

  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={pressed === undefined ? undefined : !!pressed}
      title={disabled ? (tooltip || 'Not yet available') : (tooltip || label)}
      className={`flex items-center justify-center rounded-md border transition-all duration-150 flex-shrink-0 ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        ${sizeClass}
        ${active ? 'ribbon-btn-active' : (disabled ? 'border-transparent' : inactiveColor)}`}
      style={active ? activeStyle : { color: light ? C.text2 : C.text2 }}
    >
      {showIcon && <Icon size={16} strokeWidth={1.8} />}
      {showLabel && <span className={`leading-none whitespace-nowrap ${displayMode === 'icon' ? 'hidden' : ''} ${showIcon ? 'text-[10px]' : 'text-[11px]'}`}>{label}</span>}
    </button>
  );
}

export function RibbonDivider() {
  const { C } = useTheme();
  return <div className="w-px h-8 mx-2 flex-shrink-0" style={{ background: C.border }} />;
}

// ─── Main Ribbon Component ───
export default function GisRibbon(props) {
  const { C } = useTheme();
  const [activeTab, setActiveTab] = useState('map');
  const [displayMode, setDisplayMode] = useState(() => localStorage.getItem('ribbon-display') || 'both');

  const changeDisplay = (next) => {
    setDisplayMode(next);
    try { localStorage.setItem('ribbon-display', next); } catch { /* storage unavailable */ }
  };

  const CurrentModeIcon = DISPLAY_MODES.find(m => m.id === displayMode)?.icon || LayoutGrid;

  const renderContent = () => {
    switch (activeTab) {
      case 'file': return <FileRibbon {...props} displayMode={displayMode} />;
      case 'map': return <MapRibbon {...props} displayMode={displayMode} />;
      case 'insert': return <InsertRibbon onImportFile={props.onImportFile} onAddService={props.onAddService} onGeoreference={props.onGeoreference} onShowInversion={props.onShowInversion} showInversionPanel={props.showInversionPanel} displayMode={displayMode} />;
      case 'edit': return <EditRibbon {...props} displayMode={displayMode} />;
      case 'analysis': return <AnalysisRibbon {...props} displayMode={displayMode} />;
      case 'view': return <ViewRibbon {...props} displayMode={displayMode} />;
      case 'reports': return <ReportsRibbon onShowMapLayout={props.onShowMapLayout} onShowReportBuilder={props.onShowReportBuilder} onShowProjectReport={props.onShowProjectReport} onQuickExport={props.onQuickExport} displayMode={displayMode} />;
      case 'share': return <ShareRibbon {...props} displayMode={displayMode} />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col" style={{ background: C.bg1, borderBottom: `1px solid ${C.border}` }}>
      {/* Tab bar */}
      <div className="flex items-center h-8 px-2 gap-0" style={{ borderBottom: `1px solid ${C.border}` }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 h-full text-[12px] font-medium border-b-2 transition-colors cursor-pointer
              ${activeTab === tab.id
                ? 'border-current'
                : 'border-transparent hover:border-slate-500'}`}
            style={{ color: activeTab === tab.id ? C.accent : C.text2 }}
          >
            {tab.label}
          </button>
        ))}

        {/* Display mode toggle (right-aligned) */}
        <div className="ml-auto flex items-center gap-1">
          <label className="flex items-center gap-1 px-1 h-6 rounded text-[10px]" style={{ color: C.text2 }} title="Ribbon button display">
            <CurrentModeIcon size={12} />
            <select
              aria-label="Ribbon display"
              value={displayMode}
              onChange={e => changeDisplay(e.target.value)}
              className="h-6 text-[10px] rounded outline-none cursor-pointer"
              style={{ background: C.bg4, border: `1px solid ${C.border}`, color: C.text1 }}
            >
              {DISPLAY_MODES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      {/* Ribbon content */}
      {renderContent()}
    </div>
  );
}
